import type { Ref } from 'vue'
import type { LocationQueryRaw } from 'vue-router'

export interface FiltersDraftOptions<TFilters> {
  /** The filters the list is read with: the store's, written from the URL by the page middleware. */
  applied: () => TFilters
  onApply: (filters: TFilters) => void
  onReset: () => void
  toQuery: (filters: TFilters) => LocationQueryRaw
  /** No filter control may be used while this is true. */
  loading: () => boolean
}

export interface FiltersDraftReturn<TFilters> {
  refDraft: Ref<TFilters>
  isLoading: ComputedRef<boolean>
  patchDraft: (patch: Partial<TFilters>) => void
  search: () => Promise<void>
  clearFilters: () => void
}

/** Filters are plain values (strings, numbers, lists, null), so a JSON round trip copies them off the store. */
const copyFilters = <TFilters>(filters: TFilters): TFilters => JSON.parse(JSON.stringify(filters))

/**
 * The legacy filter bars: every filter change is a list request, so the controls only prepare the filters, Search
 * applies them and Clear filters resets them at once. Applying goes through the URL; `page` is dropped so a narrower
 * list never lands on a page that no longer exists, and the page refetches on the query change.
 */
export const useFiltersDraft = <TFilters extends object>(
  options: FiltersDraftOptions<TFilters>,
): FiltersDraftReturn<TFilters> => {
  const router = useRouter()
  const route = useRoute()

  const refDraft = ref(copyFilters(options.applied())) as Ref<TFilters>
  const isLoading = computed<boolean>(() => options.loading())

  const syncQuery = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        ...options.toQuery(options.applied()),
        page: undefined,
      },
    })
  }

  const patchDraft = (patch: Partial<TFilters>): void => {
    refDraft.value = { ...refDraft.value, ...patch }
  }

  const search = async (): Promise<void> => {
    options.onApply(copyFilters(refDraft.value))
    await syncQuery()
  }

  const clearFilters = (): void => {
    options.onReset()
    refDraft.value = copyFilters(options.applied())
    void syncQuery()
  }

  // Back/forward and links change the applied filters without the controls: show what is applied.
  watch(options.applied, (filters) => {
    refDraft.value = copyFilters(filters)
  }, { deep: true })

  return {
    refDraft,
    isLoading,
    patchDraft,
    search,
    clearFilters,
  }
}
